import "./styles.scss";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Topbar from "../../components/sections/DetailsTopBar/DetailsTopBar";
import GoogleMapReact from "google-map-react";
import MapCardSlider from "../../components/sections/MapCardSlider/MapCardSlider";
import { useLocation, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { getAppConfig } from "../../redux/slices/appconfigSlice";
import LocationGray from '../../assets/images/locationGray.svg';
import LocationGreen from '../../assets/images/LocationGreen.svg';
import AppConfig from "../../helpers/config";
import { useNavigate } from "react-router-dom";
import { navigateToDetailPage } from "../../helpers/common";
import { getLoginConfig } from "../../redux/slices/loginconfigSlice";
import { getSearchFilters } from "../../redux/slices/searchFilterSlice";
import { isMobile, isTablet } from 'react-device-detect';

const MapPage = () => {
    const dispatch = useDispatch();
    const { hotelId, type } = useParams(); // Get the numeric ID from the URL
    const navigate = useNavigate();
    const location = useLocation();
    // location.state is lost on a hard refresh or a direct/shared link, but the
    // :type segment carries the same information in the URL itself — fall back to
    // it so a refreshed chat-offer map link doesn't silently look like a regular one.
    const isChatOfferFromState = location.state?.isChatOffer !== undefined
        ? location.state.isChatOffer
        : type === 'chatOffer';
    const isChatOfferRef = useRef(isChatOfferFromState);
    const flightNumbersStringFromUrl = location.state?.flightNumbersStringFromUrl;
    const sharedFlightHotelIdFromState = location.state?.flightHotelId;

    // Stabilize isChatOffer on mount, don't let it flip unexpectedly
    if (isChatOfferFromState !== undefined) {
        isChatOfferRef.current = isChatOfferFromState;
    }
    const isChatOffer = isChatOfferRef.current;
    console.log({ isChatOffer }, { hotelId }, { type });

    const appConfig = useSelector(getAppConfig);
    const searchfilters = useSelector(getSearchFilters);
    const loginconfig = useSelector(getLoginConfig);

    const offers = useMemo(() => {
        if (type === 'fav') {
            return [searchfilters?.selectedFavoriteHotel];
        }

        const branchMatch = appConfig?.branchData && String(appConfig.branchData.id) === String(hotelId);
        if (branchMatch) {
            return [appConfig.branchData];
        }

        // Explicitly check if isChatOffer is true
        if (isChatOffer === true) {
            // Find the target hotel by hotelId
            const targetHotel = appConfig?.chatGPTOffers?.find(f => String(f?.id) === String(hotelId));
            if (!targetHotel || !targetHotel?.guidForBooking) {
                return [];
            }

            // Filter all offers with the same guidForBooking
            return appConfig.chatGPTOffers.filter(f => f.guidForBooking === targetHotel?.guidForBooking);
        }

        // Default to regular offers (when isChatOffer is false or undefined)
        return appConfig.offers || [];
    }, [isChatOffer, appConfig.branchData, appConfig.chatGPTOffers, appConfig.offers, hotelId]);

    const selected = offers?.find(f => String(f?.id) === String(hotelId));

    // Fallback if offers are not loaded (e.g., direct deep link)
    // useEffect(() => {
    //     if (!offers || offers.length === 0) {
    //         console.warn('No offers available. Redirecting to home.');
    //         navigate("/", { replace: true });
    //     }
    // }, [offers, navigate]);

    const [hotelLocations, setLocations] = useState([]);
    const [mapInstance, setMapInstance] = useState(null);
    const [mapsApi, setMapsApi] = useState(null);
    const overlaysRef = useRef(new Map());

    const defaultProps = useMemo(() => ({
        center: {
            lat: Number(selected?.location?.geoLocation?.latitude) || 0.0,
            lng: Number(selected?.location?.geoLocation?.longitude) || 0.0
        }, // Default center (fallback)
        zoom: 14,
    }), []);

    const [mapCenter, setMapCenter] = useState(defaultProps.center);

    useEffect(() => {
        if (!(isMobile || isTablet)) {
            window.scroll(0, 0);
        }

        // Only update markers if we have valid offers and the selected hotel exists in them
        if (offers?.length && selected?.id) {
            const locations = offers.map(f => {
                const geo = f?.location?.geoLocation;
                return {
                    id: f.id,
                    lat: Number(geo?.latitude),
                    lng: Number(geo?.longitude),
                    name: f?.name,
                    selected: String(f.id) === String(hotelId),
                };
            });
            setLocations(locations);
        }

        const timer = setTimeout(() => {
            if (selected?.location?.geoLocation) {
                setMapCenter({
                    lat: Number(selected.location.geoLocation.latitude),
                    lng: Number(selected.location.geoLocation.longitude),
                });
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [hotelId, offers, selected]);

    const handleMarkerClick = useCallback((locationId) => {
        if (!locationId) return;
        navigate(`/hotel/${locationId}/map/${isChatOffer ? 'chatOffer' : 'normal'}`, { replace: true, state: { isChatOffer } });
    }, [navigate, isChatOffer]);

    useEffect(() => {
        if (!mapInstance || !mapsApi) return;

        const buildMarkerElement = (loc, isSelected) => {
            const container = document.createElement("div");
            container.className = "marker-container";
            container.style.cursor = "pointer";

            if (isSelected && loc?.name) {
                const label = document.createElement("div");
                label.textContent = loc.name;
                label.style.position = "absolute";
                label.style.top = "-25px";
                label.style.backgroundColor = "var(--orange)";
                label.style.padding = "7px 8px";
                label.style.borderRadius = "12px 12px 12px 0px";
                label.style.color = "var(--white)";
                label.style.fontFamily = "var(--poppings), serif";
                label.style.fontSize = "var(--fs-13)";
                label.style.height = "fit-content";
                container.appendChild(label);
            }

            const img = document.createElement("img");
            img.src = isSelected ? LocationGreen : LocationGray;
            img.alt = "marker-icon";
            container.appendChild(img);

            return container;
        };

        const createOverlay = (loc, isSelected) => {
            const overlay = new mapsApi.OverlayView();
            overlay.position = new mapsApi.LatLng(Number(loc.lat), Number(loc.lng));
            overlay.div = null;
            overlay.onAdd = () => {
                overlay.div = buildMarkerElement(loc, isSelected);
                overlay.div.addEventListener("click", () => handleMarkerClick(loc.id));
                const panes = overlay.getPanes();
                panes.overlayMouseTarget.appendChild(overlay.div);
            };
            overlay.draw = () => {
                if (!overlay.div) return;
                const projection = overlay.getProjection();
                if (!projection) return;
                const point = projection.fromLatLngToDivPixel(overlay.position);
                if (!point) return;
                overlay.div.style.position = "absolute";
                overlay.div.style.left = `${point.x}px`;
                overlay.div.style.top = `${point.y}px`;
                overlay.div.style.transform = "translate(-50%, -100%)";
            };
            overlay.onRemove = () => {
                if (overlay.div && overlay.div.parentNode) {
                    overlay.div.parentNode.removeChild(overlay.div);
                }
                overlay.div = null;
            };
            overlay.setMap(mapInstance);
            return overlay;
        };

        const updateOverlay = (overlay, loc, isSelected) => {
            overlay.position = new mapsApi.LatLng(Number(loc.lat), Number(loc.lng));
            if (overlay.div) {
                const nextDiv = buildMarkerElement(loc, isSelected);
                nextDiv.addEventListener("click", () => handleMarkerClick(loc.id));
                overlay.div.replaceWith(nextDiv);
                overlay.div = nextDiv;
            }
            if (overlay.draw) {
                overlay.draw();
            }
        };

        const nextIds = new Set(hotelLocations.map((loc) => loc.id));
        overlaysRef.current.forEach((overlay, id) => {
            if (!nextIds.has(id)) {
                overlay.setMap(null);
                overlaysRef.current.delete(id);
            }
        });

        hotelLocations.forEach((loc) => {
            if (!loc?.lat || !loc?.lng || !loc?.id) return;
            const isSelected = String(loc.id) === String(hotelId);
            const existingOverlay = overlaysRef.current.get(loc.id);
            if (existingOverlay) {
                updateOverlay(existingOverlay, loc, isSelected);
            } else {
                const overlay = createOverlay(loc, isSelected);
                overlaysRef.current.set(loc.id, overlay);
            }
        });
    }, [handleMarkerClick, hotelId, hotelLocations, mapInstance, mapsApi]);

    useEffect(() => {
        return () => {
            overlaysRef.current.forEach((overlay) => overlay.setMap(null));
            overlaysRef.current.clear();
        };
    }, []);

    const handleMapBackClick = () => {
        navigateToDetailPage(
            selected,
            dispatch,
            loginconfig,
            searchfilters,
            navigate,
            "hotel",
            true,
            type === 'fav' ? true : false,
            isChatOffer ? true : false,
            appConfig?.branchData,
            flightNumbersStringFromUrl && selected?.id === sharedFlightHotelIdFromState ? flightNumbersStringFromUrl : null,
        );
    }

    return (
        <div className="main-wrapper flight-page map-page">
            <main className="body-wrapper">
                <div className="map-page-content">
                    <div className={'hotels-container map-desktop'}>
                        <MapCardSlider handleOnView={handleMapBackClick} isChatOffer={isChatOffer} isDesktop={true} />
                    </div>

                    <div className="map-wrapper">
                        <div className="top-bar-container">
                            <Topbar isMap={true} handleMapBackClick={handleMapBackClick} />
                        </div>
                        <div className="google-map-container">
                            <GoogleMapReact
                                bootstrapURLKeys={{ key: AppConfig.googleMapAPIKey }}
                                center={mapCenter}
                                defaultCenter={defaultProps.center}
                                defaultZoom={defaultProps.zoom}
                                yesIWantToUseGoogleMapApiInternals
                                onGoogleApiLoaded={({ map, maps }) => {
                                    setMapInstance(map);
                                    setMapsApi(maps);
                                }}
                                options={{
                                    fullscreenControl: false,
                                }}
                            />
                        </div>
                        <div className="mobile-sec-map mapcards-slider">
                            <MapCardSlider handleOnView={handleMapBackClick} isChatOffer={isChatOffer} isDesktop={false} isMobile={true} />
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default MapPage;
